import { useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import DataSectionTitle from '@App/components/DataSectionTitle/DataSectionTitle'
import TypeToggles from '@App/components/TypeToggles/TypeToggles'
import TrapsToggles from '@App/components/TrapsToggles/TrapsToggles'
import SourceToggles from '@App/components/SourceToggles/SourceToggles'
import TimeSelector from '@App/components/TimeSelector/TimeSelector'
import { sexes } from '@App/pages/MosquitoObservations/mosquito-observations.constants'
import { fetchAllSources, fetchUniqueSpeciesByMission, fetchMosquitoSourcesByMission } from '@services/data.service'
import {
  setSelectionRange, setSourcesActiveIds, setMosquitoTypeInactiveTypeIds,
  setMosquitoSexActiveIds, toggleSmartTrapFilterId,
} from '@App/pages/MosquitoObservations/mosquito-observations.slice'

import { getActiveIdsFromAll, getNextInactiveIds, resolveActiveIds, getNextActiveIds } from './mosquito-observations-filter.utils'

import './mosquito-observations-filter.styl'

function MosquitoObservationsFilter() {
  const dispatch = useDispatch()
  const { activeProject } = useSelector(state => state.missionSetup)
  const {
    selectionRange, sourcesActiveIds, mosquitoTypeInactiveTypeIds, mosquitoSexActiveIds,
    smartTrapFilterIds,
  } = useSelector(state => state.mosquitoDetection)

  const { mosquitoSpecies = [] } = useOutletContext()

  const { data: allSources } = useQuery({
    queryKey: ['sources'],
    queryFn: () => fetchAllSources(),
  })

  const { data: relevantSourceIds } = useQuery({
    queryKey: ['relevantSources', activeProject?.id, selectionRange?.startDate, selectionRange?.endDate],
    queryFn: () => fetchMosquitoSourcesByMission(activeProject?.id, selectionRange?.startDate, selectionRange?.endDate),
    enabled: !!activeProject?.id && !!selectionRange?.startDate && !!selectionRange?.endDate,
  })

  const sources = useMemo(() => {
    if (!relevantSourceIds || !allSources) return []
    return allSources.filter(source => relevantSourceIds.includes(source.id))
  }, [allSources, relevantSourceIds])

  const sourcesActiveNames = useMemo(() => {
    const sourceIds = sources?.map(source => source.id) || []
    return resolveActiveIds(sourceIds, sourcesActiveIds)
  }, [sources, sourcesActiveIds])

  const setSourceActiveNames = (nextActiveSourceIds) => {
    const sourceIds = sources?.map(source => source.id) || []
    return setSourcesActiveIds(
      getNextActiveIds(sourceIds, nextActiveSourceIds),
    )
  }

  const { data: species, isLoading: speciesIsLoading } = useQuery({
    queryKey: [
      'uniqueActiveSpecies', activeProject?.id, selectionRange.startDate, selectionRange.endDate, sourcesActiveIds,
      mosquitoSexActiveIds, smartTrapFilterIds,
    ],
    queryFn: () => fetchUniqueSpeciesByMission(
      activeProject?.id,
      selectionRange.startDate,
      selectionRange.endDate,
      sourcesActiveIds,
      mosquitoSexActiveIds,
      smartTrapFilterIds,
    ),
    enabled: !!activeProject?.id,
  })

  const speciesOptions = useMemo(() => {
    const normalizeSpeciesName = value => String(value || '').trim().toLowerCase()
    const colorBySpeciesName = new Map(
      mosquitoSpecies.map(species => [
        normalizeSpeciesName(species?.id || species?.label),
        species?.color,
      ]),
    )

    return (species || []).map(({ name, color, count }) => ({
      id: name,
      label: name,
      group: 'other',
      icon: 'mosquito',
      color: colorBySpeciesName.get(normalizeSpeciesName(name)) || color,
      amount: count,
    }))
  }, [mosquitoSpecies, species])

  const speciesActiveIds = useMemo(() => {
    const speciesIds = speciesOptions?.map(type => type.id) || []
    return getActiveIdsFromAll(speciesIds, mosquitoTypeInactiveTypeIds)
  }, [speciesOptions, mosquitoTypeInactiveTypeIds])

  const setSpeciesActiveIds = (nextActiveTypeIds) => {
    return setMosquitoTypeInactiveTypeIds(
      getNextInactiveIds(speciesActiveIds, nextActiveTypeIds, mosquitoTypeInactiveTypeIds),
    )
  }

  return (
    <form className="mosquito-observations-filter">
      <DataSectionTitle>Select time window</DataSectionTitle>
      <TimeSelector
        selectionRange={selectionRange}
        setSelectionRange={range => dispatch(setSelectionRange(range))}
      />
      <DataSectionTitle>Available sources</DataSectionTitle>
      <SourceToggles
        sources={sources}
        sourcesActiveNames={sourcesActiveNames}
        setSourceActiveNames={setSourceActiveNames}
      />
      <DataSectionTitle>Trap status</DataSectionTitle>
      <TrapsToggles
        smartTrapFilterIds={smartTrapFilterIds}
        toggleSmartTrapFilterId={id => dispatch(toggleSmartTrapFilterId(id))}
      />
      <DataSectionTitle>Identified sex</DataSectionTitle>
      <TypeToggles
        types={sexes}
        activeTypeIds={mosquitoSexActiveIds}
        setActiveTypeIds={setMosquitoSexActiveIds}
        badge={false}
      />
      <DataSectionTitle>Identified species</DataSectionTitle>
      {(speciesOptions && speciesOptions?.length > 0)
        ? (
            <TypeToggles
              types={speciesOptions}
              activeTypeIds={speciesActiveIds}
              setActiveTypeIds={setSpeciesActiveIds}
              layout="list"
            />
          )
        : (
            <div className="mosquito-observations-filter__no-data-message">
              {speciesIsLoading ? 'Loading species data...' : 'No mosquito data found for the selected filters.'}
            </div>
          )}
    </form>
  )
}

export default MosquitoObservationsFilter
